import React, { FC, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { BookingQuery } from "../../graphql/booking.hooks";
import {
  CalendarEvent,
  CheckLg,
  Clock,
  Copy,
  GeoAlt,
  Person,
} from "react-bootstrap-icons";

import WhatsappIc from "./Images/whatsapp.svg";
import TelegramIc from "./Images/telegram.svg";
import FacebookIc from "./Images/facebook.svg";
import EmailIc from "./Images/envelope.svg";

const ME_BOOKING_URL = process.env.REACT_APP_ME_BOOKING_URL;

type ReservationItem = NonNullable<
  BookingQuery["user"]["reservations"]["items"]
>[number];

type Props = {
  showModal: boolean;
  onCancel?: () => void;
  reservations: ReservationItem[];
  uuid: string;
};

export const BookingShareModal: FC<Props> = ({
  showModal,
  onCancel,
  reservations,
  uuid,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const reservationData = reservations.find((r) => r.uuid === uuid);
  const restaurantName =
    reservationData?.restaurant?.name ??
    reservationData?.customPlace?.name ??
    "";
  const restaurantAddress =
    reservationData?.restaurant?.fullAddress ??
    reservationData?.customPlace?.location ??
    "";
  const restaurantLink =
    reservationData?.restaurant?.link || ME_BOOKING_URL || "";

  const formatDate = (iso: string) => moment(iso).format("MMMM D, YYYY");
  const formatTime = (iso: string) => moment(iso).format("h:mm A");

  const buildShareText = (withLink = false) => {
    const date = reservationData?.reservationAt
      ? formatDate(reservationData.reservationAt)
      : "";
    const time = reservationData?.reservationAt
      ? formatTime(reservationData.reservationAt)
      : "";
    const guests = reservationData?.guestsCount ?? 0;
    const lines = [
      `${t("booking.share.reservationAt")} ${restaurantName}`,
      `📍 ${restaurantAddress}`,
      `📅 ${date}`,
      `⏰ ${time}`,
      `👥 ${guests} ${guests > 1 ? t("booking.guests") : t("booking.guest")}`,
      "",
      t("booking.share.bookedVia"),
    ];
    if (withLink && restaurantLink) lines.push(restaurantLink);
    return lines.join("\n");
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(buildShareText(true)).then(() => {
      window.gtag?.("event", "share_copied");
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const encodedLink = encodeURIComponent(restaurantLink);
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(buildShareText(true))}`;
  const shareText = buildShareText();
  const telegramUrl = `https://t.me/share/url?url=${encodedLink}&text=${encodeURIComponent(shareText)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}&quote=${encodeURIComponent(shareText)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent("My restaurant reservation")}&body=${encodeURIComponent(buildShareText(true))}`;

  return (
    <Modal show={showModal} onHide={onCancel} centered>
      <Modal.Header closeButton className="border-0">
        <h4>{t("booking.share.title")}</h4>
      </Modal.Header>
      <Modal.Body className="text-dark px-4 text-center">
        <p className="text-gray text-start">
          {t("booking.share.yourReservationAt")}{" "}
          <span className="text-dark fw-bold">{restaurantName}</span>
        </p>
        <div className="rounded bg-nav p-4 mb-3 text-start">
          <div className="d-flex mb-3">
            <span>
              <GeoAlt className="fs-6 me-2" />
            </span>
            <p className="mb-0">{restaurantAddress}</p>
          </div>
          <div className="d-flex mb-3 align-items-center">
            <CalendarEvent className="fs-6 me-2" />
            <p className="mb-0">
              {reservationData?.reservationAt &&
                formatDate(reservationData.reservationAt)}
            </p>
          </div>
          <div className="d-flex align-items-center mb-3">
            <Clock className="fs-6 me-2" />
            <p className="mb-0">
              {reservationData?.reservationAt &&
                formatTime(reservationData.reservationAt)}
            </p>
            {reservationData?.flexibility !== "NONE" && (
              <span className="fs-7 ms-2 px-2 h-100 rounded-3 bg-primary-light text-primary">
                ±{reservationData?.flexibility.slice(5, 6)}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center">
            <Person className="fs-6 me-2" />
            <p className="mb-0">
              {reservationData?.guestsCount}{" "}
              {reservationData?.guestsCount > 1
                ? t("booking.guests")
                : t("booking.guest")}
            </p>
          </div>
        </div>
        <div className="mb-4 d-flex justify-content-center align-items-center">
          <span>{t("booking.share.bookedVia1")}</span>
          <img
            src={`${ME_BOOKING_URL}/build/images/logo-main.svg`}
            alt="ME-Booking"
            className="ms-2"
            width="96"
            height="25"
          />
        </div>
        <Button
          variant="primary"
          className="w-100 btn-lg fw-bold mb-5"
          onClick={handleCopy}
        >
          {copied ? (
            <CheckLg className="me-2 text-white" />
          ) : (
            <Copy className="me-2 text-white" />
          )}
          {copied ? t("booking.share.copied") : t("booking.share.copyDetails")}
        </Button>

        <p>{t("booking.share.shareVia")}</p>
        <div className="d-flex gap-4 justify-content-center">
          <Button
            as="a"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-circle btn btn-violet p-2"
            onClick={() => {
              window.gtag?.("event", "share_clicked");
              window.gtag?.("event", "share_whatsapp");
            }}
          >
            <img src={WhatsappIc} alt="Whatsapp" />
          </Button>
          <Button
            as="a"
            href={telegramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-circle btn btn-violet p-2"
            onClick={() => {
              window.gtag?.("event", "share_clicked");
              window.gtag?.("event", "share_telegram");
            }}
          >
            <img src={TelegramIc} alt="Telegram" />
          </Button>
          <Button
            as="a"
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-circle btn btn-violet p-2"
            onClick={() => {
              window.gtag?.("event", "share_clicked");
              window.gtag?.("event", "share_facebook");
            }}
          >
            <img src={FacebookIc} alt="Facebook" />
          </Button>
          <Button
            as="a"
            href={mailUrl}
            rel="noopener noreferrer"
            className="rounded-circle btn btn-violet p-2"
            onClick={() => {
              window.gtag?.("event", "share_clicked");
              window.gtag?.("event", "share_mail");
            }}
          >
            <img src={EmailIc} alt="Email" />
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};
